import React from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { Carousel } from "@atoms/Carousel";
import { Section } from "@atoms/Section";
import Button from "@ds/components/Button";
import Text from "@ds/components/Text";
import type { IBannerProps } from "../../../interface";
import useStyles from "../styles";
import DynamicImage from "@ds/components/DynamicImage";
import { isAllowInterest } from "@config/feature";
import LCPImage from "@ds/components/DynamicImage/LCPImage";

type BigHeroSectionProps = {
  banner?: IBannerProps[];
  isDesktop?: boolean;
};

interface IBannerImage {
  index: number;
  loadAllImage: boolean;
  item: IBannerProps;
  handleLcpLoad?: () => void;
}

const checkButtonInterestAllowed = (link?: string) => {
  const linkToFormInterest = link?.includes("/interest-form");
  if (!linkToFormInterest) return true;
  const allowed = isAllowInterest && linkToFormInterest;
  return allowed;
};

const BigHeroSection: React.FC<BigHeroSectionProps> = ({ banner, isDesktop }) => {
  const classes = useStyles({ isVisible: true });
  const router = useRouter();
  const [getBannerWidth, setBannerWidth] = React.useState(1200);
  const [isCarouselReady, setIsCarouselReady] = React.useState(false);
  const [loadAllImage, setLoadAllImage] = React.useState(false);

  const updateBannerWidth = () => {
    const elementWidth = window.document.querySelector("#root-banner");
    if (elementWidth) {
      const resizeObserver = new ResizeObserver(() => {
        setBannerWidth(elementWidth.clientWidth);
      });
      resizeObserver.observe(elementWidth);
    }
  };

  React.useEffect(() => {
    const defaultWidth = window.document.getElementById("root-banner");
    if (defaultWidth) {
      setBannerWidth(defaultWidth.getBoundingClientRect().width);
    }
  }, [getBannerWidth]);

  React.useEffect(() => {
    window.addEventListener("orientationchange", updateBannerWidth);
    return () => {
      window.removeEventListener("orientationchange", updateBannerWidth);
    };
  }, []);

  const _renderFullBanner = (param: IBannerImage) => {
    const { item, index, loadAllImage } = param;
    const show = index === 0 || loadAllImage;

    return (
      <Box
        display="flex"
        alignItems="center"
        paddingX={isDesktop ? "120px" : "20px"}
        key={index}
        height="100%"
        position="relative"
      >
        {show && (
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            sx={{
              backgroundImage: `linear-gradient(
              90deg,
              rgba(255, 255, 255, 100) 30%,
              rgba(255, 255, 255, 75) 50%,
              rgba(255, 255, 255, 0) 80%),
            url(${item.imageFull?.mediaPath + "?tr=w-1200"})`,
              backgroundSize: "contain",
              backgroundPosition: "right",
              backgroundRepeat: "no-repeat",
              zIndex: 0,
            }}
          />
        )}
        <Container maxWidth="lg" disableGutters sx={{ mb: 4, position: "relative", zIndex: 1 }} id="root-banner">
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={3}
          >
            <Grid item xs={6}>
              {item.localizations
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .filter((val: any) => val.language === router.locale)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .map((item: any) => (
                  <>
                    <Text
                      color="primary-main"
                      className={classes.subtitle}
                      variant="h2"
                      weight="700"
                    >
                      {item.titleHead}
                    </Text>
                    <Text className={classes.title} variant="h1" weight="700">
                      {item.title}
                    </Text>
                    <Text
                      component="div"
                      color="general-mid"
                      className={classes.text}
                      variant="body1"
                    >
                      <Box dangerouslySetInnerHTML={{ __html: item.description }} />
                    </Text>
                    <Box display="flex" gap={2}>
                      {item.mainButton && checkButtonInterestAllowed(item.mainButton?.link) && (
                        <Link href={item.mainButton.link}>
                          <Button tabIndex={-1}>{item.mainButton.label}</Button>
                        </Link>
                      )}
                      {item.optionalButton.label !== "" && checkButtonInterestAllowed(item.optionalButton?.link) && (
                        <Link href={item.optionalButton.link}>
                          <Button tabIndex={-1} variant="secondary">
                            {item.optionalButton.label}
                          </Button>
                        </Link>
                      )}
                    </Box>
                  </>
                ))}
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  };

  const _renderSquareBanner = (param: IBannerImage) => {
    const { item, index, handleLcpLoad } = param;
    return (
      <Box
        height="100%"
        paddingX={isDesktop ? "120px" : "20px"}
        display="flex"
        alignItems="center"
        key={index}
      >
        <Container maxWidth="lg" disableGutters sx={{ mb: 4 }} id="root-banner">
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={3}
          >
            <Grid item xs={7}>
              {item.localizations
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .filter((val: any) => val.language === router.locale)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .map((item: any) => (
                  <>
                    <Text
                      color="primary-main"
                      className={classes.subtitle}
                      variant="h2"
                      weight="700"
                    >
                      {item.titleHead}
                    </Text>
                    <Text className={classes.title} variant="h1" weight="700">
                      {item.title}
                    </Text>
                    <Text
                      component="div"
                      color="general-mid"
                      className={classes.text}
                      variant="body1"
                    >
                      <Box dangerouslySetInnerHTML={{ __html: item.description }} />
                    </Text>
                    <Box display="flex" gap={2}>
                      {item.mainButton && checkButtonInterestAllowed(item.mainButton?.link) && (
                        <Link href={item.mainButton.link}>
                          <Button tabIndex={-1}>{item.mainButton.label}</Button>
                        </Link>
                      )}
                      {item.optionalButton.label !== "" && checkButtonInterestAllowed(item.optionalButton?.link) && (
                        <Link href={item.optionalButton.link}>
                          <Button tabIndex={-1} variant="secondary">
                            {item.optionalButton.label}
                          </Button>
                        </Link>
                      )}
                    </Box>
                  </>
                ))}
            </Grid>
            <Grid item xs={5}>
              {index === 0
                ? (
                  <LCPImage
                    src={`${item.imageSquare?.mediaPath}`}
                    alt={`${item.imageSquare?.mediaName}`}
                    width={486}
                    height={486}
                    quality={75}
                    onLoad={handleLcpLoad}
                  />
                ) : (
                  <DynamicImage
                    src={`${item.imageSquare?.mediaPath + "?tr=w-486"}`}
                    alt={`${item.imageSquare?.mediaName}`}
                    width={486}
                    height={486}
                    rounded
                    priority
                    onLoad={handleLcpLoad}
                  />
                )}
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  };

  /**
   * Deterministic trigger:
   * - Fired ONLY after LCP image is fully loaded
   * - Then deferred until browser is idle / next paint
   */
  const handleLcpLoad = React.useCallback(() => {
    const trigger =
      "requestIdleCallback" in window
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? (cb: () => void) => (window as any).requestIdleCallback(cb)
        : (cb: () => void) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(cb);
          });
        };

    trigger(() => setIsCarouselReady(true));
  }, []);

  if (!banner || banner.length === 0) return null;

  return (
    <Section
      maxWidth="full-width"
      pt={0}
      pb={6}
      position="relative"
      sx={{
        height: "36rem",
        ".slick-slide": {
          height: "36rem",
        },
        ".slick-slide > div": {
          height: "100%",
        },
        ".slick-dots": {
          bottom: 25,
          // left: `calc((100vw - 1200px) / 2)`,
          left: `calc((100vw - ${getBannerWidth}px) / 2)`,
          width: "auto",
        },
        ".slick-dots li > button": {
          height: "auto",
        },
        ".slick-dots li > button:before": {
          content: "'-'",
        },
      }}
    >
      {!isCarouselReady
        ? (
          <div style={{ display: "flex", justifyContent: "center", height: "36rem" }}>
            {banner[0].isFullImage
              ? _renderFullBanner({ item: banner[0], index: 0, loadAllImage })
              : _renderSquareBanner({
                item: banner[0],
                index: 0,
                handleLcpLoad,
                loadAllImage,
              })}
          </div>
        ) : (
          <Carousel
            arrowPosition="3vw"
            settings={{
              dots: true,
              beforeChange: () => {
                if (loadAllImage) return;
                setLoadAllImage(true);
              },
            }}
          >
            {banner?.map((item: IBannerProps, index: number) => (
              <>
                {item?.isFullImage
                  ? _renderFullBanner({ item, index, loadAllImage })
                  : _renderSquareBanner({ item, index, handleLcpLoad, loadAllImage })}
              </>
            ))}
          </Carousel>
        )}
    </Section>
  );
};

export default BigHeroSection;
